import useScheduleRealtime from '../../realtime/useScheduleRealtime';
import React, { useState, useRef, useEffect } from "react";
import { useSelector, useDispatch } from "react-redux";
import Table from "react-bootstrap/Table";
import Modal from "react-bootstrap/Modal";
import Alert from "react-bootstrap/Alert";
import Spinner from "react-bootstrap/Spinner";
import ScheduleJobModal from "./ScheduleJobModal";
import AccountingModal from "./AccountingModal";
// Import centralized date utilities
import { formatDateLocalEnhanced } from "../../utils/dateUtils";
import { hasAnyEditPermission } from './utils/schedulePermissions';
import { fetchData } from '../../redux/slices/apiCallSlice';
import { selectApiUrls } from '../../redux/slices/apiUrlSlice';
import { getProjectId, getProjectStatus } from '../../utils/dataNormalization';
import SortableTableHeader from "./SortableTableHeader";
import { getNextSortConfig, sortTableRows } from "./utils/tableSorting";
import CompactScheduleSection from "./components/CompactScheduleSection";

const INITIAL_SORT_CONFIG = {
  key: null,
  direction: "ascending",
};

const ReleasePendingCSO = ({ filteredJobs = null, canEdit = true }) => {
  const dispatch = useDispatch();
  // Get data from Redux store instead of making a separate API call
  const jobScheduleDataFromRedux = useSelector((state) => state.jobSchedule.data);
  const jobScheduleData = filteredJobs || jobScheduleDataFromRedux;
  const apiUrls = useSelector(selectApiUrls);
  const [releasePendingApiJobs, setReleasePendingApiJobs] = useState([]);
  const [releasePendingSellPrices, setReleasePendingSellPrices] = useState({});
  const [hasLoadedApiJobs, setHasLoadedApiJobs] = useState(false);
  const [loadingApiJobs, setLoadingApiJobs] = useState(false);
  const [apiJobsError, setApiJobsError] = useState(null);
  
  const userRoles = useSelector((state) => state.user?.userRoles) || [];
  const userRolesMapped = useSelector((state) => state.user?.rolesMapped) || {};
  
  const hasEditAccess = Boolean(canEdit) && hasAnyEditPermission(userRoles);
  
  const [showModal, setShowModal] = useState(false);
  const [selectedJob, setSelectedJob] = useState(null);
  const [showAccountingModal, setShowAccountingModal] = useState(false);
  const [accountingJob, setAccountingJob] = useState(null);
  const [visibleRows, setVisibleRows] = useState({
    qualifiedExceptions: 50,
    pendingCSO: 50,
    engineeringOnly: 50,
    longLeadOnly: 50
  });
  const [sortConfigs, setSortConfigs] = useState({
    qualifiedExceptions: INITIAL_SORT_CONFIG,
    pendingCSO: INITIAL_SORT_CONFIG,
    engineeringOnly: INITIAL_SORT_CONFIG,
    longLeadOnly: INITIAL_SORT_CONFIG,
  });
  const containerRef = useRef(null);

  useScheduleRealtime({ view: 'release', blocked: loadingApiJobs, apply: (rows, snapshots) => {
      setReleasePendingApiJobs(rows); setHasLoadedApiJobs(true); setApiJobsError(null);
      const prices = {};
      snapshots.forEach(({ core }) => Object.values(core).forEach(item => {
        const job = item?.GeneralProjectInfo || item?.generalProjectInfo || item;
        const price = Array.isArray(item?.PriceBreakdown) ? item.PriceBreakdown[0] : item?.PriceBreakdown;
        if (getProjectId(job) && price?.SellPriceAmount != null) prices[getProjectId(job)] = price.SellPriceAmount;
      }));
      setReleasePendingSellPrices(prices);
    } });

  useEffect(() => {
    let isMounted = true;

    const loadReleasePendingJobs = async () => {
      if (!apiUrls?.fullSchedule) {
        return;
      }

      setLoadingApiJobs(true);
      setApiJobsError(null);

      try {
        // Fetch jobs for all 4 status types
        const statuses = [
          'Release with Qualified Exceptions',
          'Release Pending CSO',
          'Release with CSO Pending',
          'Release w/ CSO Pending',
          'Engineering Only',
          'Long Lead Only'
        ];

        const fetchPromises = statuses.map(status => {
          const queryJoiner = apiUrls.fullSchedule.includes("?") ? "&" : "?";
          const url = `${apiUrls.fullSchedule}${queryJoiner}jobStatus=${encodeURIComponent(status)}`;
          return dispatch(fetchData({ url })).unwrap();
        });

        const results = await Promise.allSettled(fetchPromises);

        let hasUnexpectedError = false;
        const allSellPrices = {};

        // Combine all results and normalize; treat 404 "No projects found" as empty data.
        const allJobs = results.flatMap((result) => {
          if (result.status === "fulfilled") {
            const response = result.value;
            const raw = response?.data || response || {};
            const sourceItems = Array.isArray(raw) ? raw : Object.values(raw);

            sourceItems.forEach((item) => {
              const job = item?.GeneralProjectInfo || item;
              const projectId = getProjectId(job);
              const priceBreakdownRecord = Array.isArray(item?.PriceBreakdown)
                ? item.PriceBreakdown[0]
                : item?.PriceBreakdown;

              if (projectId && priceBreakdownRecord?.SellPriceAmount !== undefined && priceBreakdownRecord?.SellPriceAmount !== null) {
                allSellPrices[projectId] = priceBreakdownRecord.SellPriceAmount;
              }
            });

            return sourceItems
              .map((item) => item?.GeneralProjectInfo || item)
              .filter(Boolean);
          }

          const reason = result.reason || {};
          const statusCode = reason?.status ?? reason?.error?.status;
          const errorText = [
            reason?.message,
            reason?.details?.error,
            reason?.data?.error,
            reason?.error,
          ]
            .filter(Boolean)
            .join(" ")
            .toLowerCase();

          const isNoProjects404 = statusCode === 404 && errorText.includes("no projects found");
          if (!isNoProjects404) {
            hasUnexpectedError = true;
            console.error("ReleasePendingCSO: Unexpected status fetch error:", reason);
          }

          return [];
        });

        if (isMounted) {
          setReleasePendingApiJobs(allJobs);
          setReleasePendingSellPrices(allSellPrices);
          setHasLoadedApiJobs(true);
          if (hasUnexpectedError) {
            setApiJobsError('Failed to load some release pending job sections.');
          }
        }
      } catch (error) {
        console.error('ReleasePendingCSO: Failed to load jobs from full schedule endpoint:', error);
        if (isMounted) {
          setApiJobsError('Failed to load release pending jobs.');
          setHasLoadedApiJobs(true);
        }
      } finally {
        if (isMounted) {
          setLoadingApiJobs(false);
        }
      }
    };

    loadReleasePendingJobs();

    return () => {
      isMounted = false;
    };
  }, [dispatch, apiUrls?.fullSchedule]);

  // Helper function to normalize job status names
  const normalizeJobStatus = (status) => {
    if (!status) return status;
    
    // Map variations to standard names
    const statusMap = {
      "Release with Qualified Exceptions": "Release with Qualified Exceptions",
      "Release With Qualified Exceptions": "Release with Qualified Exceptions",
      "Release Pending CSO": "Release Pending CSO",
      "Release with CSO Pending": "Release Pending CSO",
      "Release With CSO Pending": "Release Pending CSO",
      "Release w/ CSO Pending": "Release Pending CSO",
      "Engineering Only": "Engineering Only",
      "Engineering Release Only": "Engineering Only",
      "Long Lead Only": "Long Lead Only",
      "Long Leads Only": "Long Lead Only",
      "Long Leads Release Only": "Long Lead Only"
    };
    
    return statusMap[status] || status;
  };
  // Helper function to format dates in local time - use centralized utility
  const formatDateLocal = (dateValue) => formatDateLocalEnhanced(dateValue);

  // Format currency
  const formatCurrency = (value) => {
    const numValue = parseFloat(value) || 0;
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(numValue);
  };

  const parseCurrencyValue = (value) => {
    if (typeof value === "number") {
      return Number.isFinite(value) ? value : 0;
    }

    const normalizedValue = String(value ?? "").replace(/[$,]/g, "");
    const parsedValue = parseFloat(normalizedValue);
    return Number.isFinite(parsedValue) ? parsedValue : 0;
  };

  const getSellPriceValue = (job) => {
    const projectId = getProjectId(job);

    return releasePendingSellPrices[projectId]
      ?? job.SellPriceAmount
      ?? job.SellPrice
      ?? 0;
  };

  const compactFields = [
    { key: "ProjectName", label: "Project Name", className: "compact-inline-project-column" },
    { key: "ModelNumber", label: "Model Number", className: "compact-inline-model-column" },
    { key: "Scheduled", label: "Scheduled", render: (job) => job.CurrentPlanShipDate ? "Yes" : "No" },
    { key: "OrderDate", label: "Order Date", render: (job) => formatDateLocal(job.OrderDate) },
    { key: "RequestedDeliveryDate", label: "Requested Delivery", render: (job) => formatDateLocal(job.RequestedDeliveryDate) },
    { key: "SalesRep", label: "Sales Rep" },
    { key: "State", label: "State" },
    { key: "JobStatus", label: "Job Status" },
    { key: "PaymentTerms", label: "Payment Terms" },
    { key: "SellPrice", label: "Sell Price", render: (job) => formatCurrency(getSellPriceValue(job)) },
    { key: "Notes", label: "Notes", className: "notes-column" },
  ];

  const tableColumns = [
    {
      key: "Scheduled",
      label: "Scheduled",
      type: "text",
      getValue: (job) => job.CurrentPlanShipDate ? "Yes" : "No",
    },
    { key: "JobNumber", label: "Job Number", type: "text" },
    { key: "ProjectName", label: "Project Name", type: "text" },
    { key: "ModelNumber", label: "Model Number", type: "text" },
    { key: "OrderDate", label: "Order Date", type: "date" },
    { key: "ProductionRelease", label: "Release To Scheduling", type: "date" },
    { key: "RequestedDeliveryDate", label: "Requested Delivery Date", type: "date" },
    { key: "SalesRep", label: "Sales Rep", type: "text" },
    { key: "State", label: "State", type: "text" },
    { key: "JobStatus", label: "Job Status", type: "text" },
    { key: "PaymentTerms", label: "Payment Terms", type: "text" },
    { key: "SellPrice", label: "Sell Price", type: "currency", getValue: getSellPriceValue },
    { key: "Notes", label: "Notes", type: "text" },
  ];

  const getSellPriceTotal = (jobs) => (
    jobs.reduce((sum, job) => sum + parseCurrencyValue(getSellPriceValue(job)), 0)
  );

  const handleRowDoubleClick = (job) => {
    // Check if user has accounting role and show accounting modal
    if (userRolesMapped && userRolesMapped.Accounting && !userRolesMapped.Admin && !userRolesMapped.Scheduling) {
      setAccountingJob(job);
      setShowAccountingModal(true);
      return;
    }
    
    if (hasEditAccess) {
      setSelectedJob(job);
      setShowModal(true);
    }
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setSelectedJob(null);
  };

  const handleScroll = () => {
    if (containerRef.current) {
      const { scrollTop, scrollHeight, clientHeight } = containerRef.current;
      if (scrollTop + clientHeight >= scrollHeight - 50) {
        // Update visible rows for all sections
        setVisibleRows((prev) => ({
          qualifiedExceptions: prev.qualifiedExceptions + 50,
          pendingCSO: prev.pendingCSO + 50,
          engineeringOnly: prev.engineeringOnly + 50,
          longLeadOnly: prev.longLeadOnly + 50
        }));
      }
    }
  };

  useEffect(() => {
    const container = containerRef.current;
    if (container) {
      container.addEventListener("scroll", handleScroll);
      return () => container.removeEventListener("scroll", handleScroll);
    }
  }, []);

  // Use API jobs when available (already filtered by backend), otherwise fall back to Redux data
  const dataToUse = hasLoadedApiJobs ? releasePendingApiJobs : jobScheduleData;

  // Filter jobs by each status type
  const getJobsByStatus = (statusVariations) => {
    return Array.isArray(dataToUse)
      ? dataToUse.filter(
          (job) =>
            statusVariations.includes(normalizeJobStatus(getProjectStatus(job))) &&
            job.HideFromList !== true &&
            job.HideFromList !== "true" &&
            job.ActualShipDate == null
        )
        .sort((a, b) => {
          // Sort unscheduled items first
          const aScheduled = a.CurrentPlanShipDate ? 1 : 0;
          const bScheduled = b.CurrentPlanShipDate ? 1 : 0;
          return aScheduled - bScheduled;
        })
      : [];
  };

  const qualifiedExceptionsJobs = getJobsByStatus([
    "Release with Qualified Exceptions",
    "Release With Qualified Exceptions"
  ]);

  const pendingCSOJobs = getJobsByStatus([
    "Release Pending CSO",
    "Release with CSO Pending",
    "Release w/ CSO Pending"
  ]);

  const engineeringOnlyJobs = getJobsByStatus([
    "Engineering Only",
    "Engineering Release Only"
  ]);

  const longLeadOnlyJobs = getJobsByStatus([
    "Long Lead Only",
    "Long Leads Only",
    "Long Leads Release Only"
  ]);

  const totalVisibleJobs =
    qualifiedExceptionsJobs.length +
    pendingCSOJobs.length +
    engineeringOnlyJobs.length +
    longLeadOnlyJobs.length;

  const totalSellPrice =
    getSellPriceTotal(qualifiedExceptionsJobs) +
    getSellPriceTotal(pendingCSOJobs) +
    getSellPriceTotal(engineeringOnlyJobs) +
    getSellPriceTotal(longLeadOnlyJobs);

  const shouldShowApiJobsError = Boolean(apiJobsError) && totalVisibleJobs === 0;

  const handleSort = (sectionKey, key) => {
    setSortConfigs((currentConfigs) => ({
      ...currentConfigs,
      [sectionKey]: getNextSortConfig(currentConfigs[sectionKey], key),
    }));
    setVisibleRows((currentRows) => ({
      ...currentRows,
      [sectionKey]: 50,
    }));
    if (containerRef.current) {
      containerRef.current.scrollTop = 0;
    }
  };

  const renderJobTable = (data, visibleCount, sectionKey, sectionTitle) => {
    const sortedData = sortTableRows(data, tableColumns, sortConfigs[sectionKey]);
    const visibleData = sortedData.slice(0, visibleCount);
    const tableSellPrice = getSellPriceTotal(data);
    
    return (
      <>
        <div className="schedule-simple-desktop-table">
          <div style={{ fontWeight: "600", marginBottom: "8px" }}>
            Total Sell Price: {formatCurrency(tableSellPrice)}
          </div>
          <Table striped bordered hover responsive className="schedule-responsive-table">
            <SortableTableHeader
              columns={tableColumns}
              sortConfig={sortConfigs[sectionKey]}
              onSort={(key) => handleSort(sectionKey, key)}
            />
            <tbody>
              {visibleData.length > 0 ? (
                visibleData.map((job) => (
                  <tr
                    key={job.ID}
                    className="schedule-collapsible-row"
                    onDoubleClick={() => handleRowDoubleClick(job)}
                    style={{ cursor: hasEditAccess ? 'pointer' : 'default' }}
                    title={hasEditAccess ? "Double-click to edit" : ""}
                  >
                    <td data-label="Scheduled" className="schedule-card-primary" style={{
                      backgroundColor: job.CurrentPlanShipDate ? '#28a745' : 'transparent',
                      color: job.CurrentPlanShipDate ? 'white' : 'inherit',
                      fontWeight: 'bold',
                      textAlign: 'center'
                    }}>
                      {job.CurrentPlanShipDate ? 'Yes' : 'No'}
                    </td>
                    <td data-label="Job Number" className="schedule-card-primary">{job.JobNumber}</td>
                    <td data-label="Project Name" className="schedule-card-primary">{job.ProjectName}</td>
                    <td data-label="Model Number" className="schedule-card-primary">{job.ModelNumber}</td>
                    <td data-label="Order Date" className="schedule-card-secondary">{formatDateLocal(job.OrderDate)}</td>
                    <td data-label="Production Release" className="schedule-card-secondary">{formatDateLocal(job.ProductionRelease)}</td>
                    <td data-label="Requested Delivery" className="schedule-card-secondary">{formatDateLocal(job.RequestedDeliveryDate)}</td>
                    <td data-label="Sales Rep" className="schedule-card-secondary">{job.SalesRep}</td>
                    <td data-label="State" className="schedule-card-secondary">{job.State}</td>
                    <td data-label="Status" className="schedule-card-primary">{job.JobStatus}</td>
                    <td data-label="Payment Terms" className="schedule-card-secondary">{job.PaymentTerms}</td>
                    <td data-label="Sell Price" className="schedule-card-secondary" style={{ textAlign: 'right' }}>{formatCurrency(getSellPriceValue(job))}</td>
                    <td data-label="Notes" className="schedule-card-secondary">{job.Notes}</td>
                    {(hasEditAccess || (userRolesMapped && userRolesMapped.Accounting)) && (
                      <td data-label="Actions" className="schedule-card-action-cell">
                        <button type="button" className="btn btn-primary schedule-card-action" onClick={() => handleRowDoubleClick(job)}>
                          Open job
                        </button>
                      </td>
                    )}
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan="13" className="schedule-empty-row">No data available</td>
                </tr>
              )}
            </tbody>
          </Table>
        </div>
        <div className="schedule-simple-compact-list">
          <CompactScheduleSection
            title={`${sectionTitle} (${data.length})`}
            jobs={visibleData}
            dateLabel="Production Release"
            getDateValue={(job) => formatDateLocal(job.ProductionRelease)}
            fields={compactFields}
          />
        </div>
        {visibleCount < sortedData.length && (
          <div style={{ textAlign: "center", padding: "10px" }}>
            Loading more rows...
          </div>
        )}
      </>
    );
  };

  return (
    <div
      className="release-pending-cso-container"
      style={{ overflow: "auto", maxHeight: "80vh" }}
      ref={containerRef}
    >
      {loadingApiJobs && (
        <div style={{ textAlign: "center", padding: "20px" }}>
          <Spinner animation="border" role="status">
            <span className="visually-hidden">Loading release pending jobs...</span>
          </Spinner>
          <p>Loading release pending jobs...</p>
        </div>
      )}

      {shouldShowApiJobsError && (
        <Alert variant="danger">
          {apiJobsError}
        </Alert>
      )}

      {!loadingApiJobs && (
        <>
          {totalVisibleJobs === 0 && (
            <Alert variant="info">No release pending jobs found.</Alert>
          )}

          {totalVisibleJobs > 0 && (
            <div
              style={{
                fontWeight: "700",
                fontSize: "1.1rem",
                marginBottom: "16px",
              }}
            >
              Total Sell Price for All Items: {formatCurrency(totalSellPrice)}
            </div>
          )}

          {/* Release with Qualified Exceptions Section */}
          {qualifiedExceptionsJobs.length > 0 && (
            <div className="mb-5">
              <h4 className="schedule-simple-desktop-only">Release with Qualified Exceptions ({qualifiedExceptionsJobs.length})</h4>
              {renderJobTable(qualifiedExceptionsJobs, visibleRows.qualifiedExceptions, "qualifiedExceptions", "Release with Qualified Exceptions")}
            </div>
          )}

          {/* Release Pending CSO Section */}
          {pendingCSOJobs.length > 0 && (
            <div className="mb-5">
              <h4 className="schedule-simple-desktop-only">Release Pending CSO ({pendingCSOJobs.length})</h4>
              {renderJobTable(pendingCSOJobs, visibleRows.pendingCSO, "pendingCSO", "Release Pending CSO")}
            </div>
          )}

          {/* Engineering Only Section */}
          {engineeringOnlyJobs.length > 0 && (
            <div className="mb-5">
              <h4 className="schedule-simple-desktop-only">Engineering Only ({engineeringOnlyJobs.length})</h4>
              {renderJobTable(engineeringOnlyJobs, visibleRows.engineeringOnly, "engineeringOnly", "Engineering Only")}
            </div>
          )}

          {/* Long Lead Only Section */}
          {longLeadOnlyJobs.length > 0 && (
            <div className="mb-5">
              <h4 className="schedule-simple-desktop-only">Long Lead Only ({longLeadOnlyJobs.length})</h4>
              {renderJobTable(longLeadOnlyJobs, visibleRows.longLeadOnly, "longLeadOnly", "Long Lead Only")}
            </div>
          )}
        </>
      )}

      <Modal 
        className="job-schedule-modal"
        show={showModal} 
        onHide={() => {
          // Get reference to the modal component
          const modalComponent = document.querySelector(".modal-content")
            ?.closest(".modal");
          
          // Dispatch a click event on the ScheduleJobModal's internal close handler
          // which will trigger the confirmation dialog if there are unsaved changes
          const closeButton = modalComponent?.querySelector(".modal-header .btn-close");
          if (closeButton) {
            closeButton.click();
          } else {
            // Fallback if button not found
            handleCloseModal();
          }
        }} 
        size="xl"
      >
        <ScheduleJobModal
          selectedJob={selectedJob}
          setSelectedJob={setSelectedJob}
          handleCloseModal={handleCloseModal}
          handleSaveChanges={() => {}}
          canEdit={hasEditAccess}
        />
      </Modal>
      
      {/* Accounting Modal for accounting users */}
      <AccountingModal
        show={showAccountingModal}
        onHide={() => setShowAccountingModal(false)}
        job={accountingJob}
      />
    </div>
  );
};

export default ReleasePendingCSO;
